import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API_BASE });

// Akses token sesi di localStorage lewat 3 fungsi ini saja: kalau penyimpanan
// browser diblokir (mis. cookie/situs diblokir total, mode privat tertentu),
// localStorage melempar error -- dibungkus try/catch supaya halaman tetap tampil
// (anggap belum login), bukan layar putih.
const TOKEN_KEY = "simonev_token";

export const bacaToken = () => {
    try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
};

export const simpanToken = (token) => {
    try { localStorage.setItem(TOKEN_KEY, token); } catch { /* penyimpanan diblokir: halaman tetap jalan, tapi login tidak bisa bertahan */ }
};

export const hapusToken = () => {
    try { localStorage.removeItem(TOKEN_KEY); } catch { /* penyimpanan diblokir: tidak ada yg perlu dihapus */ }
};

api.interceptors.request.use((config) => {
    const token = bacaToken();
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Endpoint alur pra-autentikasi: 401 di sini berarti "kredensial/kode salah",
// BUKAN "sesi sudah mati" -- biarkan halaman pemanggilnya (Login/VerifyOtp)
// tangani sendiri lewat try/catch masing-masing, jangan trigger logout global.
const AUTH_FLOW_PATHS = [
    "/auth/login", "/auth/verify-otp", "/auth/resend-otp",
    "/auth/forgot-password", "/auth/reset-password/confirm",
    "/auth/verify-totp", "/auth/totp/enroll/start", "/auth/totp/enroll/confirm",
];

api.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error.response?.status;
        const url = error.config?.url || "";
        const isAuthFlowCall = AUTH_FLOW_PATHS.some((p) => url.includes(p));
        if (status === 401 && !isAuthFlowCall) {
            // AuthContext (di dalam BrowserRouter) yang menangani event ini --
            // file ini murni JS di luar React, jadi tidak bisa panggil useNavigate
            // langsung di sini.
            window.dispatchEvent(new Event("auth:session-expired"));
        }
        return Promise.reject(error);
    },
);

// Client TERPISAH khusus form publik Peer Assessment (isi nilai PPDS, tanpa akun
// AURA) -- SENGAJA tidak pakai `api` di atas: interceptor-nya menempelkan token
// admin (simonev_token) ke SETIAP request tanpa syarat, jadi kalau form publik ini
// kebetulan dibuka di browser yang sama dengan sesi admin yang masih login, token
// admin itu akan menimpa Authorization header yang kita set manual di sini. Dengan
// client terpisah (tanpa interceptor), form_token milik PPDS selalu yang terpakai.
export const peerFormApi = axios.create({ baseURL: API_BASE });

export function withPeerFormToken(token) {
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

// Client TERPISAH khusus halaman publik Survei Kepuasan (/survei) -- alasan sama dgn peerFormApi: token admin
// tidak boleh ikut terkirim, dan 401 di sini berarti sesi survei berakhir, bukan sesi admin.
export const surveiApi = axios.create({ baseURL: API_BASE });

// Ambil nama file dari header "Content-Disposition" (mis. 'attachment; filename="aura_peserta_..._2026-10-03.xlsx"').
// Backend adalah satu-satunya sumber nama file export (lihat _build_export_filename
// di backend/server.py) -- fallback hanya dipakai kalau header hilang (mis. proksi lama
// yang belum di-deploy ulang).
function _filenameFromContentDisposition(disposition, fallback) {
    if (!disposition) return fallback;
    const match = /filename\*?=(?:UTF-8'')?"?([^;"]+)"?/i.exec(disposition);
    return match ? decodeURIComponent(match[1]) : fallback;
}

// Helper terpusat utk memicu unduhan dari response axios `responseType:"blob"`.
// Nama file SELALU diambil dari Content-Disposition yang dikirim backend (expose_headers
// di CORS sudah mengizinkan ini terbaca lintas-origin Cloudflare Pages <-> Railway) --
// jangan hardcode nama file lagi di tiap halaman.
export function downloadBlobResponse(res, fallbackFilename) {
    const filename = _filenameFromContentDisposition(res.headers?.["content-disposition"], fallbackFilename);
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
}

// Terjemahkan `loc` dari 1 error Pydantic (mis. ["body","rows",3,"stase"] atau
// ["body","email"]) jadi label singkat "Baris N, kolom X" / "kolom X". `rowNumFor`
// opsional: map indeks-array (0-based) -> nomor baris Excel asli (row_num), dipakai
// pemanggil yang tahu korespondensinya (mis. form import). Tanpa itu, indeks
// ditampilkan apa adanya (1-based) sebagai fallback.
function _locLabel(loc, rowNumFor) {
    if (!Array.isArray(loc) || loc.length === 0) return null;
    const parts = loc.filter((p) => p !== "body");
    const idxPos = parts.findIndex((p) => typeof p === "number");
    if (idxPos === -1) return parts.length ? `kolom ${parts.join(".")}` : null;
    const field = parts.slice(idxPos + 1).join(".");
    const rowNum = typeof rowNumFor === "function" ? rowNumFor(parts[idxPos]) : null;
    const rowLabel = rowNum != null ? `Baris ${rowNum}` : `Baris ke-${parts[idxPos] + 1}`;
    return field ? `${rowLabel}, kolom ${field}` : rowLabel;
}

// `opts.maxMessages` (default 5): batas jumlah pesan error 422 yang ditampilkan
// sebelum dirangkas jadi "dan N kesalahan lainnya". `opts.rowNumFor` lihat _locLabel.
export function formatApiErrorDetail(detail, opts = {}) {
    const { maxMessages = 5, rowNumFor } = opts;
    if (detail == null) return "Terjadi kesalahan. Coba lagi.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        const messages = detail
            .map((e) => {
                if (!e || typeof e.msg !== "string") return JSON.stringify(e);
                const label = _locLabel(e.loc, rowNumFor);
                return label ? `${label}: ${e.msg}` : e.msg;
            })
            .filter(Boolean);
        if (messages.length === 0) return "Terjadi kesalahan. Coba lagi.";
        const shown = messages.slice(0, maxMessages);
        const sisa = messages.length - shown.length;
        return shown.join(" | ") + (sisa > 0 ? ` | dan ${sisa} kesalahan lainnya` : "");
    }
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}
